// Barcode decoding: the browser's own BarcodeDetector where it has one (Chrome on Android: every code in the picture,
// wherever it sits), else ZXing, loaded on first use (its own chunk, not in the main bundle).
import { fileToCanvas, downscaleCanvas, releaseCanvas } from '@/helper/image';

let zxing = null;
export function loadZxing() {
    if (!zxing) {
        zxing = Promise.all([
            import(/* webpackChunkName: "zxing" */ '@zxing/library'),
            import(/* webpackChunkName: "zxing" */ '@zxing/browser'),
        ]).then(([lib, browser]) => ({ lib, browser }));
        zxing.catch(() => {
            zxing = null; // a failed chunk download can be retried
        });
    }
    return zxing;
}

const FORMAT_NAMES = {
    QR_CODE: 'QR',
    DATA_MATRIX: 'DataMatrix',
    CODE_128: 'Code 128',
    CODE_39: 'Code 39',
    EAN_13: 'EAN-13',
    EAN_8: 'EAN-8',
    UPC_A: 'UPC-A',
    UPC_E: 'UPC-E',
};
const NATIVE_NAMES = {
    qr_code: 'QR',
    data_matrix: 'DataMatrix',
    code_128: 'Code 128',
    code_39: 'Code 39',
    ean_13: 'EAN-13',
    ean_8: 'EAN-8',
    upc_a: 'UPC-A',
    upc_e: 'UPC-E',
};
const SQUARE_FORMATS = ['QR_CODE', 'DATA_MATRIX'];
const LINEAR_FORMATS = ['CODE_128', 'CODE_39', 'EAN_13', 'EAN_8', 'UPC_A', 'UPC_E'];
// EAN / UPC hold the product number (GTIN): the same on every unit of a model, never a unit's seria
const PRODUCT_CODES = ['EAN-13', 'EAN-8', 'UPC-A', 'UPC-E'];

export function isSquareCode(result) {
    return result.format === 'QR' || result.format === 'DataMatrix';
}

/** Square codes (QR / DataMatrix) first: on a label they usually hold the seria */
export function squareFirst(results) {
    return [...results.filter(isSquareCode), ...results.filter(r => !isSquareCode(r))];
}

let native = null;
function nativeDetector() {
    if (!native) {
        native = (async () => {
            if (typeof window === 'undefined' || typeof window.BarcodeDetector === 'undefined') return null;
            try {
                const supported = await window.BarcodeDetector.getSupportedFormats();
                // without these two (e.g. Android without Google Play services) ZXing does better
                if (!supported.includes('data_matrix') || !supported.includes('qr_code')) return null;
                return new window.BarcodeDetector({ formats: Object.keys(NATIVE_NAMES).filter(f => supported.includes(f)) });
            } catch (e) {
                return null;
            }
        })();
    }
    return native;
}

function fromNative(codes) {
    const found = [];
    for (const code of codes || []) {
        const text = cleanText(code.rawValue);
        if (text && !found.some(r => r.text === text)) found.push({ text, format: NATIVE_NAMES[code.format] || '' });
    }
    return found;
}

/** Starts downloading what the scanner will need (nothing when the browser decodes by itself) */
export function preloadDecoder() {
    nativeDetector().then(detector => (detector ? null : loadZxing()), () => loadZxing()).catch(() => {});
}

async function zxingReader(tryHarder) {
    const { lib, browser } = await loadZxing();
    const { MultiFormatReader, DecodeHintType, BarcodeFormat, BinaryBitmap, HybridBinarizer, GlobalHistogramBinarizer } = lib;
    // square codes and barcodes apart: in one MultiFormatReader the first hit wins, and a big EAN
    // next to a small DataMatrix (MikroTik boxes) always hid the DataMatrix
    const readers = [SQUARE_FORMATS, LINEAR_FORMATS].map(names => {
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, names.map(name => BarcodeFormat[name]));
        if (tryHarder) hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new MultiFormatReader();
        reader.setHints(hints);
        return reader;
    });
    return {
        /** @returns {Array<{text: string, format: string}>} square code first */
        decode(canvas, { invert = false, globalBinarizer = false, squareOnly = false } = {}) {
            let bitmap;
            try {
                let source = new browser.HTMLCanvasElementLuminanceSource(canvas);
                if (invert) source = source.invert();
                bitmap = new BinaryBitmap(globalBinarizer ? new GlobalHistogramBinarizer(source) : new HybridBinarizer(source));
            } catch (e) {
                return [];
            }
            const found = [];
            for (const reader of squareOnly ? readers.slice(0, 1) : readers) {
                try {
                    const result = reader.decodeWithState(bitmap);
                    const text = cleanText(result.getText());
                    if (text && !found.some(r => r.text === text)) {
                        found.push({ text, format: FORMAT_NAMES[BarcodeFormat[result.getBarcodeFormat()]] || '' });
                    }
                } catch (e) {
                    // nothing of this kind in this picture
                } finally {
                    reader.reset();
                }
            }
            return found;
        },
    };
}

/**
 * A reader for QR, DataMatrix, Code 128, Code 39 and EAN/UPC.
 * tryHarder: slower, also finds rotated 1D codes (photos); off for live video frames.
 * decode(canvas) resolves to every code found, square codes first.
 */
export async function createReader({ tryHarder = false } = {}) {
    let detector = await nativeDetector();
    let zx = detector ? null : await zxingReader(tryHarder);
    return {
        /** @returns {Promise<Array<{text: string, format: string}>>} */
        async decode(canvas, options = {}) {
            if (detector) {
                try {
                    return squareFirst(fromNative(await detector.detect(canvas)));
                } catch (e) {
                    detector = null; // broken on this device: ZXing from now on
                }
            }
            if (!zx) zx = await zxingReader(tryHarder);
            return zx.decode(canvas, options);
        },
    };
}

/** Drops control characters (GS1 separators etc.) and surrounding spaces */
export function cleanText(text) {
    // eslint-disable-next-line no-control-regex
    return String(text == null ? '' : text).replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '').trim();
}

/**
 * Is the decoded text usable as an equipment's seria?
 * format: the scanner's format name; EAN / UPC (product numbers) are refused. Typed text has none.
 * @returns {string} '' when fine, otherwise the Romanian reason
 */
export function rejectReason(text, { allowUrl = false, maxLength = 100, format = '' } = {}) {
    const value = cleanText(text);
    if (!value) return 'Codul citit nu conține text.';
    if (/^WIFI:/i.test(value)) return 'Acesta este codul QR pentru conectarea la Wi-Fi, nu seria echipamentului. Căutați codul de bare de lângă „S/N” sau „Serial”.';
    if (!allowUrl) {
        if (PRODUCT_CODES.includes(format)) {
            return `Acesta este codul produsului (${format}): este același pe toate echipamentele de acest model, nu seria. Scanați codul pătrat (DataMatrix / QR) sau codul de bare de lângă „S/N”.`;
        }
        if (/^https?:\/\/[^/]+\/e\/[A-Za-z0-9]{8}\/?$/i.test(value)) return 'Acesta este codul QR al etichetei Suport DTI, nu seria echipamentului.';
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /^www\./i.test(value)) return 'Codul conține o adresă web, nu seria echipamentului.';
        if (/^(BEGIN:VCARD|MECARD:|mailto:|tel:|sms:|geo:)/i.test(value)) return 'Codul conține date de contact, nu seria echipamentului.';
        if (/[\r\n]/.test(value)) return 'Codul conține mai multe rânduri de text, nu o serie.';
    }
    if (value.length > maxLength) return `Codul are ${value.length} caractere; seria poate avea cel mult ${maxLength}.`;
    return '';
}

const pause = () => new Promise(resolve => setTimeout(resolve, 0));

function makeCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    return { canvas, ctx };
}

/** Part of a canvas (fractions of its size), scaled so its long side is at most maxSide */
function crop(source, fx, fy, fw, fh, maxSide) {
    const sx = source.width * fx;
    const sy = source.height * fy;
    const sw = source.width * fw;
    const sh = source.height * fh;
    const k = Math.min(1, maxSide / Math.max(sw, sh));
    const { canvas, ctx } = makeCanvas(sw * k, sh * k);
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return canvas;
}

/** A square of `side` px centred exactly on (cx, cy), white beyond the picture's edges, scaled to at most maxSide */
function cropAround(source, cx, cy, side, maxSide) {
    const k = Math.min(1, maxSide / side);
    const { canvas, ctx } = makeCanvas(side * k, side * k);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const x0 = Math.max(0, cx - side / 2);
    const y0 = Math.max(0, cy - side / 2);
    const x1 = Math.min(source.width, cx + side / 2);
    const y1 = Math.min(source.height, cy + side / 2);
    if (x1 > x0 && y1 > y0) {
        ctx.drawImage(source, x0, y0, x1 - x0, y1 - y0, (x0 - (cx - side / 2)) * k, (y0 - (cy - side / 2)) * k, (x1 - x0) * k, (y1 - y0) * k);
    }
    return canvas;
}

function rotate(source, degrees) {
    const rad = (degrees * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const { canvas, ctx } = makeCanvas(source.width * cos + source.height * sin, source.width * sin + source.height * cos);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rad);
    ctx.drawImage(source, -source.width / 2, -source.height / 2);
    return canvas;
}

function grid(n, overlap) {
    // n×n tiles covering the picture, each overlapping its neighbours
    const size = Math.min(1, 1 / n + overlap);
    const step = n > 1 ? (1 - size) / (n - 1) : 0;
    const tiles = [];
    for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) tiles.push([col * step, row * step, size, size]);
    }
    return tiles;
}

/**
 * The most contrasted spots of the picture (fractions of its size), strongest first and spread apart:
 * a code is a dense black / white mix, while box, wall and paper are flat.
 */
function busySpots(source, count) {
    const small = downscaleCanvas(source, 320);
    const w = small.width;
    const h = small.height;
    const data = small.getContext('2d').getImageData(0, 0, w, h).data;
    releaseCanvas(small);
    // summed-area tables of the luminance and its square: the variance of any window in O(1)
    const W = w + 1;
    const sum = new Float64Array(W * (h + 1));
    const sq = new Float64Array(W * (h + 1));
    for (let y = 0; y < h; y++) {
        let rowSum = 0;
        let rowSq = 0;
        for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            const v = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
            rowSum += v;
            rowSq += v * v;
            sum[(y + 1) * W + x + 1] = sum[y * W + x + 1] + rowSum;
            sq[(y + 1) * W + x + 1] = sq[y * W + x + 1] + rowSq;
        }
    }
    const area = (t, x0, y0, x1, y1) => t[y1 * W + x1] - t[y0 * W + x1] - t[y1 * W + x0] + t[y0 * W + x0];
    const r = Math.max(3, Math.round(Math.max(w, h) * 0.02));
    const step = Math.max(2, Math.round(r / 2));
    const spots = [];
    for (let y = r; y + r < h; y += step) {
        for (let x = r; x + r < w; x += step) {
            const n = (2 * r + 1) * (2 * r + 1);
            const mean = area(sum, x - r, y - r, x + r + 1, y + r + 1) / n;
            spots.push({ x: x / w, y: y / h, px: x, py: y, score: area(sq, x - r, y - r, x + r + 1, y + r + 1) / n - mean * mean });
        }
    }
    spots.sort((a, b) => b.score - a.score);
    const minDistance = 2 * r;
    const taken = [];
    for (const spot of spots) {
        if (taken.length >= count) break;
        if (!taken.some(t => Math.abs(t.px - spot.px) < minDistance && Math.abs(t.py - spot.py) < minDistance)) taken.push(spot);
    }
    return taken;
}

const SWEEP_MS = 4000;

/**
 * ZXing finds a DataMatrix / QR only when it sits in the middle of the picture it gets:
 * crops centred on the busiest spots of the photo, until a usable one turns up (a router's Wi-Fi QR
 * does not stop it) or time runs out. add() receives each code found.
 */
async function sweepForSquareCode(big, reader, add, isUsable) {
    const started = Date.now();
    const side = Math.max(big.width, big.height) * 0.3;
    const spots = busySpots(big, 60);
    for (let i = 0; i < spots.length && Date.now() - started < SWEEP_MS; i++) {
        const canvas = cropAround(big, spots[i].x * big.width, spots[i].y * big.height, side, 640);
        const found = reader.decode(canvas, { squareOnly: true });
        releaseCanvas(canvas);
        add(found);
        if (found.some(isUsable)) return;
        if (i % 4 === 3) await pause(); // keep the spinner moving
    }
}

async function decodeWithZxing(big, isUsable) {
    const reader = await zxingReader(true);
    const whole = Math.max(big.width, big.height) > 1600 ? downscaleCanvas(big, 1600) : big;
    const found = new Map();
    const add = results => {
        for (const result of results) if (!found.has(result.text)) found.set(result.text, result);
    };
    const tryCanvas = async (canvas, options, owned = true) => {
        add(reader.decode(canvas, options));
        if (owned) releaseCanvas(canvas);
        await pause(); // keep the spinner moving
    };
    try {
        await tryCanvas(whole, {}, false);
        await tryCanvas(whole, { invert: true }, false);
        // quarters and the centre near full resolution: small codes, and the other codes of a label
        for (const [x, y, w, h] of [...grid(2, 0.15), [0.25, 0.25, 0.5, 0.5]]) {
            await tryCanvas(crop(big, x, y, w, h, 2100), {});
        }
        // nothing yet: harder cases, stopping at the first hit
        const harder = [
            () => tryCanvas(rotate(whole, 90), {}), // a 1D code standing upright
            () => tryCanvas(whole, { globalBinarizer: true }, false),
            ...grid(3, 0.12).map(([x, y, w, h]) => () => tryCanvas(crop(big, x, y, w, h, 1500), {})),
            ...[45, -45].map(degrees => () => {
                const turned = rotate(whole, degrees);
                const scaled = downscaleCanvas(turned, 1800);
                releaseCanvas(turned);
                return tryCanvas(scaled, {});
            }),
        ];
        for (const attempt of harder) {
            if (found.size) break;
            await attempt();
        }
        if (![...found.values()].some(r => isSquareCode(r) && isUsable(r))) await sweepForSquareCode(big, reader, add, isUsable);
    } finally {
        if (whole !== big) releaseCanvas(whole);
    }
    return squareFirst([...found.values()]);
}

/**
 * Every distinct code found in a photo, square codes first: the browser's detector when it has one,
 * else ZXing on the whole picture, crops (small codes), inverted colours, slanted copies,
 * and crops centred on the busiest spots for a DataMatrix / QR that isUsable(result) accepts.
 * Throws ImageError (Romanian message) when the file cannot be opened.
 * @returns {Promise<Array<{text: string, format: string}>>}
 */
export async function decodeImageFile(file, { isUsable = () => true } = {}) {
    const big = await fileToCanvas(file, 3200);
    try {
        const detector = await nativeDetector();
        if (detector) {
            try {
                const found = fromNative(await detector.detect(big));
                if (found.length) return squareFirst(found);
            } catch (e) {
                // ZXing below
            }
        }
        return await decodeWithZxing(big, isUsable);
    } finally {
        releaseCanvas(big);
    }
}
